/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jan 31 2024
 **/

import styled from "@emotion/styled";

import { ZIndex } from "@/frameworks/core/commons";
import { Icon, Row } from "@/frameworks/designStuff/components";
import { Each } from "@/frameworks/libraries/components";
import { usePopover } from "@/frameworks/core/hooks";
import EmojiData from "@/frameworks/core/components/Chat/Emoji/emoji.json";
import { RECENT_EMOJIS } from "@/modules/chat/components/SidebarContents/ChatView/ChatItem/private/components/TotalReactionPopup";
import { LocalData } from "@/frameworks/libraries";

import TotalReactionPopup from "./TotalReactionPopup";

export default function ReactionPopup({ onSelect }: { onSelect: (reaction: string) => void }) {
	const { show } = usePopover();
	const basicReactionList = EmojiData[0].emojis;

	const prevRecentUsedEmoji = LocalData.get(RECENT_EMOJIS);
	const recentEmojis = prevRecentUsedEmoji
		? [
				...prevRecentUsedEmoji
					.flatMap((slug: string) =>
						EmojiData.flatMap((category) =>
							category.emojis.filter((categoryEmoji) => categoryEmoji.slug === slug)
						)
					)
					.slice(0, 5),
				...basicReactionList.slice(0, 5 - Math.min(prevRecentUsedEmoji.length, 5))
			]
		: basicReactionList.slice(0, 5);

	const showTotalReactionPopup = (ev: React.MouseEvent<HTMLButtonElement>) => {
		show({
			popoverID: "EMOJI_TOTAL_PICKER",
			anchorEl: ev.currentTarget,
			direction: {
				x: "center",
				y: "top"
			},
			position: {
				x: 0,
				y: -44
			},
			content: (
				<TotalReactionPopup
					onSelect={(reaction) => {
						onSelect(reaction);
					}}
				/>
			),
			zIndex: ZIndex.SIDEBAR
		});
	};
	return (
		<Row gap={2}>
			<ReactionListWrapper gap={4}>
				<Each
					render={(reaction, index) => (
						<li key={index}>
							<ReactionButton
								className="chat-font"
								verticalAlign="center"
								horizonAlign="center"
								onClick={() => {
									onSelect(reaction.slug);
								}}
							>
								{reaction.emoji}
							</ReactionButton>
						</li>
					)}
					of={recentEmojis}
				/>
			</ReactionListWrapper>
			<ReactionListWrapper onClick={showTotalReactionPopup}>
				<ReactionButton verticalAlign="center" horizonAlign="center">
					<Icon name="EmojiPlus24" style={{ cursor: "pointer" }} />
				</ReactionButton>
			</ReactionListWrapper>
		</Row>
	);
}

const ReactionListWrapper = styled(Row.ul)`
	padding: 8px;
	border-radius: 8px;
	background-color: ${({ theme }) => theme.colors.neutral_00_100};
	box-shadow:
		0px 8px 16px 0px rgba(0, 0, 0, 0.2),
		0px 0px 4px 0px rgba(0, 0, 0, 0.04);
	* {
		font-size: 18px;
	}
`;

const ReactionButton = styled(Row.button)`
	font-size: 21px;
	padding: 4px;
	width: 28px;
	height: 28px;
	border-radius: 8px;
	&:hover {
		background: ${({ theme }) => theme.colors.secondary_80_8};
	}
	cursor: pointer;
`;
